/**
 * <pill-tabs-products>
 *
 * Category-pill product widget. Only the first tab is server-rendered; every
 * other tab is fetched on demand from /collections/{handle}?view=pill-tabs.
 *
 * Core Web Vitals behavior:
 *  - Nothing is requested on page load. A 5-tab widget costs the same as a
 *    single product grid until the shopper interacts.
 *  - Fetched markup is cached per handle, so re-clicking a tab never refetches.
 *  - While a tab loads, skeleton cards fill the grid at the same aspect ratio
 *    as real cards, and the panel height is locked across the swap, so tab
 *    switching cannot produce layout shift.
 *  - Optional hover prefetch (desktop only) warms the next tab during the
 *    pointer dwell so the click feels instant, without costing anything on
 *    initial load or on touch devices.
 *  - In-flight requests are aborted when the shopper switches tabs again, so
 *    rapid clicking never renders a stale response.
 */
if (!customElements.get('pill-tabs-products')) {
  class PillTabsProducts extends HTMLElement {
    constructor() {
      super();
      this.cache = {};        // handle -> array of card HTML strings
      this.controllers = {};  // handle -> AbortController
      this.prefetched = {};   // handle -> true
      this.activeIndex = 0;
    }

    connectedCallback() {
      this.perTab = parseInt(this.dataset.perTab, 10) || 12;
      this.allowPrefetch =
        this.dataset.prefetch === 'true' &&
        window.matchMedia &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      this.pills = Array.prototype.slice.call(this.querySelectorAll('.pill-tabs__pill'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-pill-panel]'));
      this.panelsWrap = this.querySelector('.pill-tabs__panels');
      this.viewAll = this.querySelector('[data-pill-view-all]');

      var self = this;

      this.pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          self.activate(parseInt(pill.dataset.tabIndex, 10));
        });

        // Keyboard: left/right arrows move between pills (standard tablist).
        pill.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          var dir = e.key === 'ArrowRight' ? 1 : -1;
          var next = (parseInt(pill.dataset.tabIndex, 10) + dir + self.pills.length) % self.pills.length;
          self.pills[next].focus();
          self.activate(next);
        });

        if (self.allowPrefetch) {
          pill.addEventListener('mouseenter', function () {
            var handle = pill.dataset.collectionHandle;
            if (!handle || self.prefetched[handle] || self.cache[handle]) return;
            self.prefetched[handle] = true;
            self.fetchTab(handle);
          });
        }
      });
    }

    _root() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    activate(index) {
      if (isNaN(index) || index === this.activeIndex) return;

      var panel = this.panels[index];
      var pill = this.pills[index];
      if (!panel || !pill) return;

      // Lock the height across the swap so the section can't collapse and
      // reflow the page below it.
      var prevPanel = this.panels[this.activeIndex];
      if (this.panelsWrap && prevPanel) {
        this.panelsWrap.style.minHeight = prevPanel.offsetHeight + 'px';
      }

      this.pills.forEach(function (p, i) {
        var on = i === index;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-selected', on ? 'true' : 'false');
        p.setAttribute('tabindex', on ? '0' : '-1');
      });

      this.panels.forEach(function (p, i) {
        var on = i === index;
        p.classList.toggle('is-active', on);
        if (on) {
          p.removeAttribute('hidden');
        } else {
          p.setAttribute('hidden', '');
        }
      });

      this.activeIndex = index;

      if (this.viewAll && panel.dataset.collectionUrl) {
        this.viewAll.setAttribute('href', panel.dataset.collectionUrl);
      }

      if (panel.dataset.loaded === 'true') {
        this.releaseHeight();
        return;
      }

      this.loadInto(panel);
    }

    releaseHeight() {
      var wrap = this.panelsWrap;
      if (!wrap) return;
      // Wait for paint so the new content has real height before unlocking.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          wrap.style.minHeight = '';
        });
      });
    }

    /** Fill a panel's grid with skeleton cards while its products load. */
    showSkeleton(panel) {
      var grid = panel.querySelector('[data-pill-grid]');
      if (!grid || grid.children.length) return;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < this.perTab; i++) {
        var sk = document.createElement('div');
        sk.className = 'pill-tabs__skeleton';
        sk.setAttribute('data-pill-skeleton', '');
        sk.innerHTML =
          '<div class="pill-tabs__skeleton-media"></div>' +
          '<div class="pill-tabs__skeleton-line"></div>' +
          '<div class="pill-tabs__skeleton-line pill-tabs__skeleton-line--short"></div>';
        frag.appendChild(sk);
      }
      grid.appendChild(frag);
    }

    async fetchTab(handle) {
      if (this.cache[handle]) return this.cache[handle];

      // Abort any earlier request for this same tab.
      if (this.controllers[handle]) {
        try { this.controllers[handle].abort(); } catch (e) {}
      }
      var controller = new AbortController();
      this.controllers[handle] = controller;

      var url = this._root() + 'collections/' + handle + '?view=pill-tabs';
      var resp = await fetch(url, { signal: controller.signal });
      if (!resp.ok) throw new Error('pill-tabs fetch failed');
      var html = await resp.text();

      var tmp = document.createElement('div');
      tmp.innerHTML = html.trim();

      var cards = Array.prototype.slice.call(tmp.querySelectorAll('product-card'));
      var out = cards.map(function (c) { return c.outerHTML; });

      this.cache[handle] = out;
      delete this.controllers[handle];
      return out;
    }

    async loadInto(panel) {
      var handle = panel.dataset.collectionHandle;
      var grid = panel.querySelector('[data-pill-grid]');
      if (!handle || !grid) { this.releaseHeight(); return; }

      this.showSkeleton(panel);

      var self = this;
      try {
        var cards = await this.fetchTab(handle);

        // The shopper may have switched away while this was in flight.
        if (parseInt(panel.dataset.tabIndex, 10) !== this.activeIndex) {
          panel.dataset.loaded = 'true';
          this.renderCards(grid, cards);
          return;
        }

        this.renderCards(grid, cards);
        panel.dataset.loaded = 'true';
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        // Fall back to a plain link rather than leaving skeletons up forever.
        grid.innerHTML = '';
        var fallback = document.createElement('p');
        fallback.className = 'pill-tabs__error';
        var link = panel.dataset.collectionUrl || (self._root() + 'collections/' + handle);
        fallback.innerHTML = 'Couldn\u2019t load these products. <a href="' + link + '">View the collection</a>.';
        grid.appendChild(fallback);
      } finally {
        this.releaseHeight();
      }
    }

    renderCards(grid, cards) {
      grid.innerHTML = '';
      var slice = cards.slice(0, this.perTab);
      var frag = document.createDocumentFragment();
      var tmp = document.createElement('div');
      slice.forEach(function (html) {
        tmp.innerHTML = html;
        var el = tmp.firstElementChild;
        if (el) frag.appendChild(el);
      });
      grid.appendChild(frag);

      // Re-bind quick add triggers for the newly injected cards.
      var qcd = document.querySelector('quick-cart-drawer');
      if (qcd && qcd._bindNewTriggers) qcd._bindNewTriggers();
      if (qcd && qcd.init) qcd.init();
    }
  }

  customElements.define('pill-tabs-products', PillTabsProducts);
}
